import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Activity, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/admin", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#6ee7b7] rounded flex items-center justify-center">
            <Activity className="w-6 h-6 text-[#0a0a0f]" />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">CRYPTO INTEL</span>
        </div>

        {/* Login Form */}
        <div className="bg-[#12121a] border border-[#2a2a3a] rounded p-6">
          <h2 className="font-heading font-semibold text-lg mb-6 text-center">Sign In</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#7a7a8e] text-xs uppercase tracking-wider font-mono">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@cryptointel.com"
                className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a] text-[#e0e0e8] placeholder:text-[#7a7a8e]/50 focus:ring-[#6ee7b7] focus:border-[#6ee7b7]"
                data-testid="login-email"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#7a7a8e] text-xs uppercase tracking-wider font-mono">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a] text-[#e0e0e8] placeholder:text-[#7a7a8e]/50 focus:ring-[#6ee7b7] focus:border-[#6ee7b7]"
                data-testid="login-password"
                required
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6ee7b7] hover:bg-[#6ee7b7]/90 text-[#0a0a0f] font-semibold h-10 mt-2"
              data-testid="login-submit"
            >
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>
          
          <p className="text-[#7a7a8e] text-xs text-center mt-4 font-mono">
            Default: admin@cryptointel.com / admin123
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
